import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Send } from "lucide-react";
import { toast } from "sonner";

// Users & Roles card for PUT /dc-dispatch-permission/settings (admin): tick the
// roles that may click Dispatch on a Delivery Challan. Admin always can.
export default function DcDispatchPermissionSettings() {
  const [available, setAvailable] = useState([]);
  const [roles, setRoles] = useState([]);
  const [saved, setSaved] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/dc-dispatch-permission/settings").then(({ data }) => {
      setAvailable(data.available_roles);
      setRoles(data.roles);
      setSaved(data.roles);
    }).catch(() => {});
  }, []);

  const toggle = (code, on) => setRoles((rs) => (on ? [...rs, code] : rs.filter((r) => r !== code)));
  const dirty = roles.length !== saved.length || roles.some((r) => !saved.includes(r));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/dc-dispatch-permission/settings", { roles });
      setRoles(data.roles);
      setSaved(data.roles);
      toast.success("Can Dispatch DC permission saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="dc-dispatch-permission-settings">
      <div className="flex items-center gap-2 mb-1">
        <Send className="w-5 h-5 text-blue-600" />
        <h3 className="font-semibold text-slate-900">Can Dispatch DC</h3>
      </div>
      <p className="text-sm text-slate-500">
        Roles ticked here see the Dispatch button on Delivery Challans and can dispatch a DC. Other DC actions are not affected.
        Administrators always have access.
      </p>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {available.map((r) => (
          <label key={r.code} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <Checkbox checked={roles.includes(r.code)} onCheckedChange={(v) => toggle(r.code, !!v)} data-testid={`dc-dispatch-role-${r.code}`} />
            {r.label}
          </label>
        ))}
      </div>
      <div className="mt-4">
        <Button onClick={save} disabled={busy || !dirty} className="bg-blue-600 hover:bg-blue-700" data-testid="dc-dispatch-permission-save">
          {busy ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}
